import { useMemo, useState } from 'react';
import { generateApiKey } from './apiKey';
import { buildAgentPrompt, HUMAN_PROMPT } from './agentPrompt';
import { TopNav } from './TopNav';

/**
 * /setup — the canonical page an AI agent reads to install the Vocal Cord
 * MCP server. Stacked layout: Human prompt (for the user to paste into
 * their agent) on top, then the full Agent markdown the agent will follow.
 *
 * Both blocks render unconditionally so a browser-driving agent sees the
 * agent prompt in the DOM without having to click anything, and a human
 * previewing the page sees what their agent will read.
 *
 * `data-vocalcord-agent-instructions` on the agent block is a stable hook
 * for tools that scrape the DOM looking for "the instructions" — semantic,
 * not just a class name.
 */
export function Setup({ apiBase }: { apiBase: string }) {
  const apiKey = useMemo(() => generateApiKey(), []);
  const agentPrompt = useMemo(
    () => buildAgentPrompt(apiBase, apiKey),
    [apiBase, apiKey],
  );

  return (
    <div className="container narrow legal">
      <TopNav />

      <h1>Set up Vocal Cord</h1>
      <p className="section-lede">
        Two views of the same flow. Paste the prompt into your AI agent,
        or hand the agent a link to this page — the markdown below is
        what the agent will follow.
      </p>

      <section className="setup-block">
        <h2>1 · For humans</h2>
        <p className="muted">
          Copy this one-liner into your AI chat. Your agent reads
          this page and configures itself.
        </p>
        <div className="prompt-card">
          <div className="prompt-text">{HUMAN_PROMPT}</div>
          <CopyButton text={HUMAN_PROMPT} />
        </div>
      </section>

      <section className="setup-block">
        <h2>2 · For agents</h2>
        <p className="muted">
          The markdown your agent should follow. A fresh API key has
          been generated for this page load — it is ready to use, no
          signup required.
        </p>
        <div className="agent-block">
          <pre
            className="prompt-agent"
            data-vocalcord-agent-instructions="true"
            aria-label="AI agent setup instructions"
          >
            <code>{agentPrompt}</code>
          </pre>
          <CopyButton text={agentPrompt} />
        </div>
      </section>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="copy-btn"
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'copied!' : 'copy'}
    </button>
  );
}
